"use client";

import Link from "next/link";
import { useCallback, useEffect, useMemo, useState } from "react";
import { signIn, signOut, useSession } from "next-auth/react";
import { useRouter } from "next/navigation";
import Dropdown from "./components/Dropdown";
import Loader from "./components/Loader";
import UsernameWithCrown from "./components/UsernameWithCrown";
import { trackEvent } from "@/lib/analytics";
import { CATEGORIES } from "@/lib/constants";

const WINDOW_STORAGE_KEY = "iregret.topRegretsWindow";
const CATEGORY_STORAGE_KEY = "iregret.topRegretsCategory";
const CATEGORY_OPTIONS = [
  { value: "all", label: "All" },
  ...CATEGORIES.map((category) => ({ value: category, label: category })),
] as const;

type RegretItem = {
  id: string;
  title: string;
  body: string;
  category: string;
  score: number;
  createdAt?: { seconds?: number } | null;
  authorUsername?: string | null;
  authorIsAdmin?: boolean;
  age?: number | null;
};

type MeResponse = {
  loggedIn: boolean;
  hasUsername: boolean;
  username?: string | null;
  isAdmin?: boolean;
  isBanned?: boolean;
  banReason?: string | null;
};

function formatRelativeTime(createdAt?: { seconds?: number; _seconds?: number } | null) {
  const seconds = createdAt?.seconds ?? createdAt?._seconds;
  if (!seconds) return "";
  const diffMs = Date.now() - seconds * 1000;
  const diffSeconds = Math.max(0, Math.floor(diffMs / 1000));
  if (diffSeconds < 45) return "Just now";
  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) return diffMinutes === 1 ? "1 minute ago" : `${diffMinutes} minutes ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return diffHours === 1 ? "1 hour ago" : `${diffHours} hours ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return diffDays === 1 ? "1 day ago" : `${diffDays} days ago`;
  const diffWeeks = Math.floor(diffDays / 7);
  return diffWeeks === 1 ? "1 week ago" : `${diffWeeks} weeks ago`;
}

function isWindowMode(value: string | null): value is "today" | "week" | "month" | "year" {
  return value === "today" || value === "week" || value === "month" || value === "year";
}

function isCategoryMode(value: string | null): value is (typeof CATEGORY_OPTIONS)[number]["value"] {
  return CATEGORY_OPTIONS.some((option) => option.value === value);
}

export default function HomePage() {
  const { data: session, status } = useSession();
  const router = useRouter();

  const [items, setItems] = useState<RegretItem[]>([]);
  const [loadingFeed, setLoadingFeed] = useState(true);
  const [feedError, setFeedError] = useState<string | null>(null);
  const [me, setMe] = useState<MeResponse | null>(null);
  const [windowMode, setWindowMode] = useState<"today" | "week" | "month" | "year">(() => {
    if (typeof window === "undefined") return "week";
    try {
      const stored = window.localStorage.getItem(WINDOW_STORAGE_KEY);
      return isWindowMode(stored) ? stored : "week";
    } catch {
      return "week";
    }
  });
  const [categoryMode, setCategoryMode] = useState<(typeof CATEGORY_OPTIONS)[number]["value"]>(() => {
    if (typeof window === "undefined") return "all";
    try {
      const stored = window.localStorage.getItem(CATEGORY_STORAGE_KEY);
      return isCategoryMode(stored) ? stored : "all";
    } catch {
      return "all";
    }
  });
  const query = useMemo(
    () => `sort=top&window=${windowMode}&category=${encodeURIComponent(categoryMode)}&limit=50`,
    [categoryMode, windowMode]
  );
  const windowOptions = [
    { value: "today", label: "Today" },
    { value: "week", label: "Week" },
    { value: "month", label: "Month" },
    { value: "year", label: "Year" },
  ];

  const loadFeed = useCallback(async () => {
    trackEvent("feed_load_started", { window: windowMode, category: categoryMode });
    setLoadingFeed(true);
    setFeedError(null);
    try {
      const res = await fetch(`/api/regrets?${query}`, { cache: "no-store" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const message = typeof data.error === "string" ? data.error : "Failed to load feed";
        throw new Error(message);
      }
      const loadedItems = data.items ?? [];
      setItems(loadedItems);
      trackEvent("feed_load_succeeded", {
        window: windowMode,
        category: categoryMode,
        count: loadedItems.length,
      });
    } catch (err) {
      setItems([]);
      setFeedError(err instanceof Error ? err.message : "Failed to load feed");
      trackEvent("feed_load_failed", { window: windowMode, category: categoryMode });
    } finally {
      setLoadingFeed(false);
    }
  }, [categoryMode, query, windowMode]);

  const loadMe = useCallback(async () => {
    const res = await fetch("/api/me", { cache: "no-store" });
    const data = await res.json();
    setMe(data);
    if (data.loggedIn && !data.hasUsername) {
      router.push("/register");
    }
  }, [router]);

  useEffect(() => {
    loadFeed();
  }, [loadFeed]);

  useEffect(() => {
    trackEvent("page_viewed", { page: "home" });
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const stored = window.localStorage.getItem(WINDOW_STORAGE_KEY);
      if (isWindowMode(stored)) {
        setWindowMode((current) => (current === stored ? current : stored));
      }
    } catch {
      // Ignore storage access errors.
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const stored = window.localStorage.getItem(CATEGORY_STORAGE_KEY);
      if (isCategoryMode(stored)) {
        setCategoryMode((current) => (current === stored ? current : stored));
      }
    } catch {
      // Ignore storage access errors.
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(WINDOW_STORAGE_KEY, windowMode);
    } catch {
      // Ignore storage write errors.
    }
  }, [windowMode]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(CATEGORY_STORAGE_KEY, categoryMode);
    } catch {
      // Ignore storage write errors.
    }
  }, [categoryMode]);

  useEffect(() => {
    if (session) loadMe();
    else setMe(null);
  }, [session, loadMe]);

  async function handleCreate() {
    trackEvent("post_regret_clicked", { isLoggedIn: !!session, isBanned: !!me?.isBanned });
    if (me?.isBanned) return;
    if (!session) {
      trackEvent("sign_in_clicked", { source: "post_regret" });
      await signIn("google");
      return;
    }

    const res = await fetch("/api/me", { cache: "no-store" });
    const data = await res.json();
    if (!data.hasUsername) {
      router.push("/register");
      return;
    }

    router.push("/regrets/new");
  }

  async function handleAuth() {
    if (session) {
      trackEvent("logout_clicked", { source: "topbar" });
      await signOut();
      return;
    }
    trackEvent("sign_in_clicked", { source: "topbar" });
    await signIn("google");
  }

  async function handleHeroSignIn() {
    trackEvent("sign_in_clicked", { source: "hero" });
    await signIn("google");
  }

  if (status === "loading")
    return (
      <div className="container loader-wrap">
        <Loader />
      </div>
    );

  return (
    <div>
      <header className="topbar">
        <Link href="/" className="brand">
          iRegret
        </Link>
        <button onClick={handleAuth} className="nav-action text-button">
          {session ? "Log out" : "Sign in"}
        </button>
      </header>
      <div className="container page-fade">
        {me?.isBanned && (
          <div className="notice">You are banned: {me.banReason || "No reason provided"}</div>
        )}

        <div className="hero">
          <div className="hero-title">A public confession wall.</div>
          <div className="btn-row">
            <button onClick={handleHeroSignIn} disabled={!!session} className="btn btn-primary">
              {session ? (
                <span className="greeting-text">
                  <svg viewBox="0 0 24 24" aria-hidden="true" className="greeting-icon">
                    <path
                      d="M12 12.75a4.25 4.25 0 1 0-4.25-4.25A4.26 4.26 0 0 0 12 12.75ZM5 20.5c0-3.04 3.4-5.5 7-5.5s7 2.46 7 5.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <UsernameWithCrown
                    username={me?.username}
                    isAdmin={me?.isAdmin}
                    fallback="there"
                  />
                </span>
              ) : (
                "Sign in"
              )}
            </button>
            <button onClick={handleCreate} disabled={!!me?.isBanned} className="btn btn-primary">
              Post a regret
            </button>
          </div>
        </div>

        <div className="divider" />

        <div className="section-head">
          <div className="section-title">Top regrets</div>
          <div className="section-filters">
            <div className="section-filter">
              <Dropdown
                value={windowMode}
                options={windowOptions}
                onChange={(value) => {
                  const selectedWindow = value as typeof windowMode;
                  trackEvent("feed_window_changed", { from: windowMode, to: selectedWindow });
                  setWindowMode(selectedWindow);
                }}
                ariaLabel="Time"
              />
            </div>
            <div className="section-filter">
              <Dropdown
                value={categoryMode}
                options={[...CATEGORY_OPTIONS]}
                onChange={(value) => {
                  const selectedCategory = value as typeof categoryMode;
                  trackEvent("feed_category_changed", { from: categoryMode, to: selectedCategory });
                  setCategoryMode(selectedCategory);
                }}
                ariaLabel="Category"
              />
            </div>
          </div>
        </div>

        {loadingFeed ? (
          <div className="notice">Loading feed...</div>
        ) : feedError ? (
          <div className="notice">{feedError}</div>
        ) : (
          <ul className="regret-list">
            {items.map((r) => {
              const timeLabel = formatRelativeTime(r.createdAt);
              return (
                <li key={r.id} className="regret-row">
                  <div className="score-pill">{r.score ?? 0}</div>
                  <div>
                    <Link href={`/regret/${r.id}`} className="row-title">
                      {r.title}
                    </Link>
                    <div className="feed-meta">
                      {timeLabel && <span>{timeLabel}</span>}
                      {r.category && <span>{r.category}</span>}
                      {r.authorUsername && (
                        <span>
                          <UsernameWithCrown
                            username={r.authorUsername}
                            isAdmin={r.authorIsAdmin}
                          />
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
            {items.length === 0 && <div className="notice">No regrets found for this filter</div>}
          </ul>
        )}

        {items.length >= 50 && (
          <div className="read-more">
            <Link href="/" className="link">
              Read more regrets &gt;
            </Link>
          </div>
        )}

        <footer className="ending-note">
          <div>Built for reflection, not perfection.</div>
          <div className="ending-note-copy">© 2026 vrintle.</div>
          <div className="social-links" aria-label="Social links">
            <a href="https://www.linkedin.com/in/vrintle/" target="_blank" rel="noreferrer" className="social-link" aria-label="LinkedIn">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M5.55 4.2h12.9c.75 0 1.35.6 1.35 1.35v12.9c0 .75-.6 1.35-1.35 1.35H5.55c-.75 0-1.35-.6-1.35-1.35V5.55c0-.75.6-1.35 1.35-1.35ZM8.02 9.08a1.08 1.08 0 1 0 0-2.16a1.08 1.08 0 0 0 0 2.16Zm-1 1.54h2v6.32h-2v-6.32Zm3.2 0h1.92v.86c.49-.63 1.23-1.03 2.13-1.03c2 0 3.18 1.32 3.18 3.7v2.79h-2.07v-2.53c0-1.14-.56-1.87-1.54-1.87c-1.04 0-1.65.78-1.65 1.97v2.43h-1.97v-6.32Z"
                  fillRule="evenodd"
                  fill="currentColor"
                />
              </svg>
            </a>
            <a href="https://www.instagram.com/vrintle/" target="_blank" rel="noreferrer" className="social-link" aria-label="Instagram">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M8.05 4.25h7.9c2.1 0 3.8 1.7 3.8 3.8v7.9c0 2.1-1.7 3.8-3.8 3.8h-7.9a3.8 3.8 0 0 1-3.8-3.8v-7.9c0-2.1 1.7-3.8 3.8-3.8Zm3.95 2.1a5.65 5.65 0 1 0 0 11.3a5.65 5.65 0 0 0 0-11.3Zm0 1.95a3.7 3.7 0 1 1 0 7.4a3.7 3.7 0 0 1 0-7.4Zm4.58-.7a1.18 1.18 0 1 0 0-2.36a1.18 1.18 0 0 0 0 2.36Z"
                  fillRule="evenodd"
                  fill="currentColor"
                />
              </svg>
            </a>
            <a href="https://x.com/vrintle" target="_blank" rel="noreferrer" className="social-link" aria-label="X">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M5.15 4.2h3.26l4.17 5.38l4.57-5.38h1.98l-5.5 6.48l5.71 8.1h-3.28l-4.4-6.15l-5.22 6.15H4.44l5.97-7.03Zm3.92 1.96H7.98l6.98 9.68h1.08Z"
                  fill="currentColor"
                />
              </svg>
            </a>
          </div>
        </footer>
      </div>
    </div>
  );
}
